import { useState, useCallback } from 'react';
import { useErrorHandler } from '../../utils/useErrorHandler';
import { User } from '../../user/types/type';
import { Order } from '../types/Order';
import dataProvider from '../../services/api';
import { ORDER_MANAGEMENT_CONSTANTS } from '../constants/orderManagement';

export const useOrderManagement = () => {
  const { handleErrorWithLogout } = useErrorHandler(); 

  // User state
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [usersPerPage, setUsersPerPage] = useState<number>(ORDER_MANAGEMENT_CONSTANTS.DEFAULT_USERS_PER_PAGE);

  // Order state
  const [orders, setOrders] = useState<Order[]>([]);
  const [orderSearchTerm, setOrderSearchTerm] = useState<string>('');
  const [currentOrderPage, setCurrentOrderPage] = useState<number>(1);
  const [totalOrders, setTotalOrders] = useState<number>(0);
  const [ordersPerPage, setOrdersPerPage] = useState<number>(ORDER_MANAGEMENT_CONSTANTS.DEFAULT_ORDERS_PER_PAGE); // Now dynamic
  const [loading, setLoading] = useState<boolean>(false);

  const handleUsersPerPageChange = useCallback((newUsersPerPage: number) => {
    if (newUsersPerPage !== usersPerPage) {
      setUsersPerPage(newUsersPerPage);
      setCurrentPage(1); 
    }
  }, [usersPerPage]);

  const handleOrdersPerPageChange = useCallback((newOrdersPerPage: number) => {
    if (newOrdersPerPage !== ordersPerPage) {
      setOrdersPerPage(newOrdersPerPage);
      setCurrentOrderPage(1); 
    }
  }, [ordersPerPage]);

  const fetchUsers = useCallback(async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      const { data: totalData } = await dataProvider.userTotalCount({
        filter: searchTerm,
        count: usersPerPage,
      });
      const totalUsers = totalData.count;

      const { data } = await dataProvider.getListUsers({
        filter: searchTerm,
        page: currentPage - 1,
        perPage: usersPerPage,
      });

      const mappedUsers = data.map((user: User) => ({
        ...user,
        name: `${user.firstName} ${user.middleName || ''} ${user.lastName}`.trim(),
        middleName: user.middleName || '',
        profileImageUrl: user.profileImageUrl ? 
          `${user.profileImageUrl}?t=${new Date().getTime()}` : '',
      }));

      setUsers(mappedUsers);
      setTotalUsers(totalUsers);

      if (mappedUsers.length > 0 && !selectedUser) {
        setSelectedUser(mappedUsers[0]);
      } else if (selectedUser) {
        const updated = mappedUsers.find((u: User) => u.userId === selectedUser.userId);
        if (updated) setSelectedUser(updated);
      }
    } catch (error: any) {
      handleErrorWithLogout(error);
      setUsers([]);
      setTotalUsers(0);
      setSelectedUser(null);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, currentPage, usersPerPage, selectedUser, loading, handleErrorWithLogout]);

  const fetchOrders = useCallback(async (userId: number) => {
    if (loading) return;
    
    setLoading(true);
    try {
      const { data, total } = await dataProvider.getOrderList('orders', {
        pagination: {
          page: currentOrderPage - 1,
          perPage: ordersPerPage,
        },
        filter: {
          postedByIds: [userId],
          fulfilledByIds: [],
          flowIds: [],
          filter: orderSearchTerm
        },
      });
      setOrders(data);
      setTotalOrders(total);
    } catch (error: any) {
      handleErrorWithLogout(error);
      setOrders([]);
      setTotalOrders(0);
    } finally {
      setLoading(false);
    }
  }, [currentOrderPage, ordersPerPage, orderSearchTerm, loading, handleErrorWithLogout]);

  return {
    // User data
    users,
    selectedUser,
    setSelectedUser,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    totalUsers,
    usersPerPage,
    
    // Order data
    orders,
    orderSearchTerm,
    setOrderSearchTerm,
    currentOrderPage,
    setCurrentOrderPage,
    totalOrders,
    ordersPerPage,
    loading,
    
    // Functions
    fetchUsers,
    fetchOrders,
    handleErrorWithLogout,
    onUsersPerPageChange: handleUsersPerPageChange,
    onOrdersPerPageChange: handleOrdersPerPageChange, // Export callback
  };
};